/**
 * frontend/src/pages/valuation/_ActionChip.tsx
 *
 * VAL-HISTORY-ICONS-1 — the icon-only action chip, shared by the Dashboard
 * and the History table (_EngagementTable). Moved out of ValuationDashboard
 * unchanged.
 */
import { Link } from 'react-router-dom'
import { ClipboardList, Eye, PenLine } from 'lucide-react'
import { NEXT_STEP_LABELS } from './_nextStep'

const SIGN_LABELS: readonly string[] = [NEXT_STEP_LABELS.signOff, NEXT_STEP_LABELS.reviewThenSignOff, NEXT_STEP_LABELS.recordCalculation]
const INTAKE_LABELS: readonly string[] = [NEXT_STEP_LABELS.startIntake, NEXT_STEP_LABELS.continueIntake, NEXT_STEP_LABELS.methodology]

export type ChipKind = 'sign' | 'intake' | 'view'
export function chipKind(label: string): ChipKind {
  if (SIGN_LABELS.includes(label)) return 'sign'
  if (INTAKE_LABELS.includes(label)) return 'intake'
  return 'view'
}
const CHIP_COLOR: Record<ChipKind, string> = {
  sign: 'border-green-700 bg-green-600 text-white hover:bg-green-500',
  intake: 'border-amber-700 bg-amber-500 text-white hover:bg-amber-400',
  view: 'border-gray-400 bg-gray-100 text-gray-700 hover:bg-gray-200',
}
const CHIP_ICON = { sign: PenLine, intake: ClipboardList, view: Eye } as const

/** Icon-only action. The words live in aria-label and a CSS tooltip (hover and
 *  keyboard focus), so the chip has no text node. */
export function ActionChip({ label, route, raised }: { label: string; route: string; raised: boolean }) {
  const kind = chipKind(label)
  const Icon = CHIP_ICON[kind]
  return (
    <Link
      to={route}
      aria-label={label}
      data-tip={label}
      data-raised={raised ? 'true' : 'false'}
      className={`relative inline-flex h-9 w-9 items-center justify-center rounded-md border ${raised ? 'border-b-4' : 'border-b'} ${CHIP_COLOR[kind]} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-gray-500 after:pointer-events-none after:absolute after:bottom-full after:right-0 after:mb-1 after:hidden after:whitespace-nowrap after:rounded after:bg-gray-900 after:px-2 after:py-1 after:text-xs after:text-white after:content-[attr(data-tip)] hover:after:block focus-visible:after:block`}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
    </Link>
  )
}
